import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';

const MANAGER_ROLES = ['sales_manager', 'sales_director', 'admin', 'it', 'ceo'];

interface UserRoleInfo {
  roleCode: string | null;
  roleName: string | null;
  appUserId: string | null;
}

async function fetchUserRole(authUserId: string): Promise<UserRoleInfo> {
  const { data: appUser, error: appUserError } = await supabase
    .from('app_users')
    .select('id, system_role_id')
    .eq('auth_user_id', authUserId)
    .maybeSingle();

  if (appUserError || !appUser) {
    return { roleCode: null, roleName: null, appUserId: null };
  }

  if (!appUser.system_role_id) {
    return { roleCode: null, roleName: null, appUserId: appUser.id };
  }

  const { data: role, error: roleError } = await supabase
    .from('system_roles')
    .select('code, name')
    .eq('id', appUser.system_role_id)
    .maybeSingle();

  if (roleError || !role) {
    return { roleCode: null, roleName: null, appUserId: appUser.id };
  }

  return {
    roleCode: role.code,
    roleName: role.name,
    appUserId: appUser.id,
  };
}

export function useCanActAsManager() {
  const { user } = useAuth();

  const roleQuery = useQuery({
    queryKey: ['userRole', user?.id],
    queryFn: () => fetchUserRole(user!.id),
    enabled: !!user?.id,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  const canActAsManager = useMemo(() => {
    if (!roleQuery.data?.roleCode) return false;
    return MANAGER_ROLES.includes(roleQuery.data.roleCode);
  }, [roleQuery.data?.roleCode]);

  return {
    canActAsManager,
    roleCode: roleQuery.data?.roleCode ?? null,
    roleName: roleQuery.data?.roleName ?? null,
    appUserId: roleQuery.data?.appUserId ?? null,
    isLoading: roleQuery.isLoading,
    error: roleQuery.error,
  };
}
